import { RefObject, useEffect } from 'react';
import type Konva from 'konva';

// The internals the mirror needs: the transform-in-progress flag and the
// move/up handlers Konva binds in the constructor (safe to call by reference).
type TransformerInternals = Konva.Transformer & {
  _transforming: boolean;
  _handleMouseMove: (event: Event) => void;
  _handleMouseUp: (event: Event) => void;
};

// Konva's Transformer registers its anchor-drag move/up listeners on the main
// window only (hardcoded `window` in _handleMouseDown). An anchor dragged
// inside the popout window never receives them there — the resize freezes on
// the mousedown. Mirrors the popout window's events into the transformer
// while one of its anchors is being dragged (same guard flag Konva uses).
export const useTransformerWindowBinding = (
  transformerRef: RefObject<Konva.Transformer>,
  targetWindow: Window | null
): void => {
  useEffect(() => {
    if (!targetWindow) {
      return;
    }

    const handleMove = (event: Event): void => {
      const transformer = transformerRef.current as TransformerInternals | null;

      if (transformer?._transforming) {
        transformer._handleMouseMove(event);
      }
    };

    const handleUp = (event: Event): void => {
      const transformer = transformerRef.current as TransformerInternals | null;

      if (transformer?._transforming) {
        transformer._handleMouseUp(event);
      }
    };

    // Same event set and phases Konva registers on the main window.
    targetWindow.addEventListener('mousemove', handleMove);
    targetWindow.addEventListener('touchmove', handleMove);
    targetWindow.addEventListener('mouseup', handleUp, true);
    targetWindow.addEventListener('touchend', handleUp, true);

    return () => {
      targetWindow.removeEventListener('mousemove', handleMove);
      targetWindow.removeEventListener('touchmove', handleMove);
      targetWindow.removeEventListener('mouseup', handleUp, true);
      targetWindow.removeEventListener('touchend', handleUp, true);
    };
  }, [transformerRef, targetWindow]);
};
